// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { SectionContainer, SectionTitle } from "@awc/App/Widgets/DesignSystem/DesignSystem.helpers";
import { fontConfig } from "@awc/XDL/PreStyle/typography";
import { color } from "@awc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import Link, { LinkProps } from "@insite/mobius/Link";
import ListItem, { OrderedList, UnorderedList } from "@insite/mobius/Lists";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import capitalize from "lodash/capitalize";
import React, { FC } from "react";
import { css } from "styled-components";

interface TypographyTabStyles {
    titleGridItem?: GridItemProps;
    gridItem?: GridItemProps;
    smallGridItem?: GridItemProps;
    formWrapper?: InjectableCss;
    linkWrapper?: InjectableCss;
    typographyWrapper?: GridContainerProps;
    viewTitle?: TypographyProps;
    headLine?: TypographyProps;
    headLineMobile?: TypographyProps;
    regular?: { typographyProps?: TypographyProps; linkProps?: LinkProps };
    small?: { typographyProps?: TypographyProps; linkProps?: LinkProps };
    xSmall?: { typographyProps?: TypographyProps; linkProps?: LinkProps };
    xxSmall?: { typographyProps?: TypographyProps; linkProps?: LinkProps };
    subSection?: InjectableCss;
    bodyCopySingle?: InjectableCss;
}

const sharedRegularStyles: TypographyProps = {
    size: fontConfig.fontSize.regular,
    lineHeight: fontConfig.lineHeight.regular,
};

const sharedSmallStyles: TypographyProps = {
    size: fontConfig.fontSize.small,
    lineHeight: fontConfig.lineHeight.small,
};

const sharedXSmallStyles: TypographyProps = {
    size: fontConfig.fontSize.xSmall,
    lineHeight: fontConfig.lineHeight.xSmall,
};

const sharedXXSmallStyles: TypographyProps = {
    size: fontConfig.fontSize.xxSmall,
    lineHeight: fontConfig.lineHeight.xxSmall,
};

const typogrphyTabStyles: TypographyTabStyles = {
    titleGridItem: {
        width: 12,
    },
    gridItem: {
        width: 12,
        css: css`
            flex-direction: column;
            padding-bottom: 15px;
        `,
    },
    smallGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            flex-wrap: wrap;
            padding-bottom: 15px;
        `,
    },
    formWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            width: 100%;
            flex-wrap: wrap;
        `,
    },
    typographyWrapper: {
        gap: 24,
        css: css`
            ${GridItemStyle} {
                > * {
                    margin-bottom: 0;
                }
            }
        `,
    },
    viewTitle: {
        variant: "h3",
        css: css`
            padding-top: 8px;
            border-top: 1px solid ${color("common.border")};
            width: 100%;
        `,
    },
    linkWrapper: {
        css: css`
            background-color: #f2f2f2;
            width: 100%;
            padding: 12px;
            font-size: ${fontConfig.fontSize.regular}px;
            line-height: ${fontConfig.lineHeight.regular};
        `,
    },
    headLine: {
        size: fontConfig.fontSize.headLine,
        lineHeight: fontConfig.lineHeight.headLine,
        weight: fontConfig.weight.bold,
        css: css`
            display: block;
            margin-bottom: 24px;
        `,
    },
    regular: {
        typographyProps: {
            ...sharedRegularStyles,
            weight: fontConfig.weight.regular,
            css: css`
                display: block;
                margin-bottom: 15px;
            `,
        },
        linkProps: {
            typographyProps: {
                ...sharedRegularStyles,
                weight: fontConfig.weight.bold,
            },
            css: css`
                margin-bottom: 15px;
            `,
        },
    },
    small: {
        typographyProps: {
            ...sharedSmallStyles,
            weight: fontConfig.weight.regular,
            css: css`
                display: block;
                margin-bottom: 14px;
            `,
        },
        linkProps: {
            typographyProps: { ...sharedSmallStyles, weight: fontConfig.weight.bold },
            css: css`
                margin-bottom: 14px;
            `,
        },
    },
    xSmall: {
        typographyProps: {
            ...sharedXSmallStyles,
            weight: fontConfig.weight.regular,
            css: css`
                display: block;
                margin-bottom: 12px;
            `,
        },
        linkProps: {
            typographyProps: { ...sharedXSmallStyles, weight: fontConfig.weight.bold },
            css: css`
                margin-bottom: 12px;
            `,
        },
    },
    xxSmall: {
        typographyProps: {
            ...sharedXXSmallStyles,
            weight: fontConfig.weight.regular,
            css: css`
                display: block;
                margin-bottom: 10px;
            `,
        },
        linkProps: {
            typographyProps: { ...sharedXXSmallStyles, weight: fontConfig.weight.bold },
            css: css`
                margin-bottom: 10px;
            `,
        },
    },
    headLineMobile: {
        size: fontConfig.fontSize.headLineMobile,
        lineHeight: fontConfig.lineHeight.headLineMobile,
        weight: fontConfig.weight.bold,
    },
    subSection: {
        css: css`
            margin-bottom: 32px;
            ${TypographyStyle}:last-child {
                margin-bottom: 0;
            }
        `,
    },
    bodyCopySingle: {
        css: css`
            display: flex;
            flex-direction: column;
            row-gap: 12px;
            ${TypographyStyle} {
                margin-bottom: 0;
            }
        `,
    },
};

const styles = typogrphyTabStyles;

const bodyCopy = [
    {
        title: "Body Copy : Regular",
        style: styles.regular,
        fontInfo: `Roboto : ${fontConfig.weight.regular} : Font-Size ${fontConfig.fontSize.regular}px : Line-Height: ${fontConfig.lineHeight.regular}`,
    },
    {
        title: "Body Copy : Small",
        style: styles.small,
        fontInfo: `Roboto : ${fontConfig.weight.regular} : Font-Size ${fontConfig.fontSize.small}px : Line-Height:  ${fontConfig.lineHeight.small}`,
    },
    {
        title: "Body Copy : xSmall",
        style: styles.xSmall,
        fontInfo: `Roboto : ${fontConfig.weight.regular} : Font-Size ${fontConfig.fontSize.xSmall}px : Line-Height: ${fontConfig.lineHeight.xSmall}`,
    },
    {
        title: "Body Copy : xxSmall",
        style: styles.xxSmall,
        fontInfo: `Roboto : ${fontConfig.weight.regular} : Font-Size ${fontConfig.fontSize.xxSmall}px : Line-Height: ${fontConfig.lineHeight.xxSmall}`,
    },
];

const TypographyTab: FC = props => {
    return (
        <SectionContainer extendedStyles={{ gridContainer: { gap: 0 } }}>
            <GridItem {...styles.titleGridItem}>
                <SectionTitle>{translate("Font Families")}</SectionTitle>
            </GridItem>
            <GridItem {...styles.gridItem}>
                <StyledWrapper {...styles.formWrapper}>
                    <Typography>{translate("Roboto")}</Typography>
                    {Object.entries(fontConfig.weight).map(([key, value]) => (
                        <Typography key={key} weight={value}>
                            {capitalize(key)}
                        </Typography>
                    ))}
                </StyledWrapper>
            </GridItem>

            <GridItem {...styles.gridItem}>
                <SectionTitle>{translate("Hierarchy")}</SectionTitle>
                <Typography variant="h1">
                    {translate(`H1 - Roboto: ${fontConfig.weight.bold} : Font-Size 40px : Line-Height 47px`)}
                </Typography>
                <Typography variant="h2">
                    {translate(`H2 - Roboto: ${fontConfig.weight.bold} : Font-Size 24px : Line-Height 28px`)}
                </Typography>
                <Typography variant="h3">
                    {translate(`H3 - Roboto: ${fontConfig.weight.bold} : Font-Size 19px : Line-Height 23px`)}
                </Typography>
                <Typography variant="h4">
                    {translate(`H4 - Roboto: ${fontConfig.weight.bold} : Font-Size 16px : Line-Height 19px`)}
                </Typography>
                <Typography variant="h5">
                    {translate(`H5 - Roboto: ${fontConfig.weight.bold} : Font-Size 16px : Line-Height 19px`)}
                </Typography>
                <Typography variant="h6">
                    {translate(`H6 - Roboto: ${fontConfig.weight.bold} : Font-Size 16px : Line-Height 19px`)}
                </Typography>
            </GridItem>

            {bodyCopy.map(({ title, style, fontInfo }) => (
                <GridItem {...styles.gridItem} key={title}>
                    <SectionTitle>{translate(title)}</SectionTitle>
                    <Typography {...style?.typographyProps} forwardAs="p">
                        {fontInfo}
                    </Typography>
                    <Typography {...style?.typographyProps} forwardAs="p">
                        Lorem ipsum dolor sit amet, consectetur adipisicing elit, sed do eiusmod tempor incididunt ut
                        labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco
                        laboris nisi ut aliquip ex ea commodo consequat.
                    </Typography>
                    <Typography {...style?.typographyProps} forwardAs="p">
                        Lorem ipsum dolor sit amet, consectetur adipisicing elit, sed do eiusmod tempor incididunt ut
                        labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco
                        laboris nisi ut aliquip ex ea commodo consequat.
                    </Typography>
                    <Link href="/" {...style?.linkProps}>
                        Text Link : Inactive
                    </Link>
                    <Link href="/" {...style?.linkProps}>
                        Text Link : Hover
                    </Link>
                </GridItem>
            ))}

            <GridItem {...styles.gridItem}>
                <SectionTitle>{translate("Body Copy : Single")}</SectionTitle>
                <StyledWrapper {...styles.bodyCopySingle}>
                    <Typography {...styles.regular?.typographyProps} forwardAs="p">
                        {`Regular : Roboto : Regular : Font-Size ${fontConfig.fontSize.regular}px : Line-Height: ${fontConfig.lineHeight.singleLineRegular}`}
                        <br /> [Example Second Line]
                    </Typography>
                    <Typography {...styles.small?.typographyProps} forwardAs="p">
                        {`Small : Roboto : Regular : Font-Size ${fontConfig.fontSize.small}px : Line-Height: ${fontConfig.lineHeight.singleLineSmall} `}
                        <br />
                        [Example Second Line]
                    </Typography>
                    <Typography {...styles.xSmall?.typographyProps} forwardAs="p">
                        {`XSmall : Roboto : Regular : Font-Size ${fontConfig.fontSize.xSmall}px : Line-Height: ${fontConfig.lineHeight.singleLinexSmall}`}
                        <br />
                        [Example Second Line]
                    </Typography>
                    <Typography {...styles.xxSmall?.typographyProps} forwardAs="p">
                        {`XXSmall : Roboto : Regular : Font-Size ${fontConfig.fontSize.xxSmall}px : Line-Height: ${fontConfig.lineHeight.singleLinexxSmall}`}
                        <br />
                        [Example Second Line]
                    </Typography>
                </StyledWrapper>
            </GridItem>

            <GridItem {...styles.gridItem}>
                <SectionTitle>Headers + Paragraph Spacing</SectionTitle>
                <StyledWrapper {...styles.subSection}>
                    <Typography variant="h1">{translate("H1 Example")}</Typography>
                    <Typography variant="h2">{translate("H2 Example")}</Typography>
                    <Typography {...styles.regular?.typographyProps} forwardAs="p">
                        "Lorem ipsum dolor sit amet, consectetur adipisicing elit, sed do eiusmod tempor incididunt ut
                        labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco
                        laboris nisi ut aliquip ex ea commodo consequat.",
                    </Typography>
                    <Typography {...styles.regular?.typographyProps} forwardAs="p">
                        "Lorem ipsum dolor sit amet, consectetur adipisicing elit, sed do eiusmod tempor incididunt ut
                        labore et dolore magna aliqua.",
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.subSection}>
                    <Typography variant="h3">{translate("H3 Example")}</Typography>
                    <Typography {...styles.regular?.typographyProps} forwardAs="p">
                        "Lorem ipsum dolor sit amet, consectetur adipisicing elit, sed do eiusmod tempor incididunt ut
                        labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco
                        laboris nisi ut aliquip ex ea commodo consequat.",
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.subSection}>
                    <Typography variant="h4">{translate("H4 Example")}</Typography>
                    <Typography variant="h5">{translate("H5 Example")}</Typography>
                    <Typography variant="h6">{translate("H6 Example")}</Typography>
                </StyledWrapper>
            </GridItem>

            <GridItem {...styles.smallGridItem}>
                <SectionTitle>Bulleted Lists</SectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <UnorderedList>
                        <ListItem>Unordered List Item</ListItem>
                        <ListItem>
                            Unordered List Item w/Sub-List
                            <UnorderedList>
                                <ListItem>Unordered Sub-List Item</ListItem>
                                <ListItem>Unordered Sub-List Item</ListItem>
                            </UnorderedList>
                        </ListItem>
                        <ListItem>Unordered List Item</ListItem>
                        <ListItem>Unordered List Item</ListItem>
                    </UnorderedList>
                </StyledWrapper>
            </GridItem>

            <GridItem {...styles.smallGridItem}>
                <SectionTitle>Numbered Lists</SectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <OrderedList>
                        <ListItem>Numbered List Item</ListItem>
                        <ListItem>
                            Numbered List Item w/Sub-List
                            <OrderedList>
                                <ListItem>Numbered Sub-List Item</ListItem>
                                <ListItem>Numbered Sub-List Item</ListItem>
                            </OrderedList>
                        </ListItem>
                        <ListItem>Numbered List Item</ListItem>
                        <ListItem>Numbered List Item</ListItem>
                    </OrderedList>
                </StyledWrapper>
            </GridItem>
        </SectionContainer>
    );
};

export default TypographyTab;
